import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Moment from 'moment';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import { navigate } from 'actionCreators';
import ScheduleType from 'types/schedule';
import { AppointmentType } from 'types/appointment';
import Loading from 'components/Loading';
import CalendarAssigneeRow from 'calendarAssigneeRow';
import { membersMatchAssignment } from '../../../../../utils/schedulingUtils';
import S from 'calendar.scss';

require('moment-range');

class SchedulingCalendar extends Component {
  onSelect = (selectedDay, selectedMember, adjustedStartTime, ssBlockage) => this.props.onSelect &&
    this.props.onSelect(selectedDay, selectedMember, adjustedStartTime, ssBlockage);

  onOpeningClick = (nextOpening, selectedMember) => this.props.onOpeningClick &&
    this.props.onOpeningClick(nextOpening, selectedMember);

  handleReschedule = appt => this.props.navigate(
    `/phases/${appt.phase.id}/appointments/${appt.id}/edit/${appt.kind}`,
  );

  isAssignedInstallerCrew = assignee => this.props.phaseSource === 'IFI' &&
    !!this.props.phaseInstallerId && assignee.installer_id === this.props.phaseInstallerId;

  isAssignedEstimator = assignee => this.props.phaseSource === 'IFI' &&
    !!this.props.phaseEstimatorId && assignee.id === this.props.phaseEstimatorId;

  isIfiMatched = (assignee) => {
    if (assignee.kind === 'Crew') return this.isAssignedInstallerCrew(assignee);
    if (assignee.kind === 'Estimator') return this.isAssignedEstimator(assignee);
    return false;
  };

  // For IFI, keep the assigned (green) estimator/installer crews at the top of the list.
  sortAssignedFirst = members => (this.props.phaseSource !== 'IFI' ? members : [
    ...members.filter(m => this.isIfiMatched(m.assignee)),
    ...members.filter(m => !this.isIfiMatched(m.assignee)),
  ]);

  membersCoverPhaseZip = (members, zip) => members.filter(m => (m.assignee.kind !== 'Crew' ||
    m.assignee.covered_zip_codes.includes(zip) ||
    m.assignee.traveling_zip_codes.includes(zip) ||
    this.isAssignedInstallerCrew(m.assignee)));

  membersMatchPhase = (members, applySsFilters, showMismatchedCrews) => members.filter(m =>
    (!applySsFilters || showMismatchedCrews || m.assignee.kind !== 'Crew' || !m.assignee.specificMismatch));

  filteredMembers = (members, phaseZip) => {
    const { applySsFilters, showMismatchedCrews,
            phaseSource, phaseEstimatorId, phaseInstallerId } = this.props;
    const membersByZip = this.membersCoverPhaseZip(members, phaseZip);
    const byPhaseMatch = this.membersMatchPhase(membersByZip, applySsFilters, showMismatchedCrews);
    const byAssignment = membersMatchAssignment(
      byPhaseMatch, phaseSource, showMismatchedCrews, phaseEstimatorId, phaseInstallerId,
    );
    return this.sortAssignedFirst(byAssignment);
  }

  render = () => {
    const { schedule: { loading, filters, members }, dates, role, isCcs,
            selectedDayStart, selectedDayEnd, pendingAppointment, phaseZip,
            selectedAssignee, selectable, isScheduleBoard, employableType,
            applySmartScheduling, relatedPhases, setDelayedField, phaseSource,
            emptyResultsMsg, manageVendorWorkrooms, phaseType,
            phaseEstimatorId, phaseInstallerId } = this.props;
    const filteredMembers = isScheduleBoard ? members : this.filteredMembers(members, phaseZip);
    const className = cx({
      [S.calendar]: true,
      [S.loading]: loading,
    }, this.props.className);
    return (<div className={className}>
      {this.props.children}
      {loading && <Loading className={S.spinner} />}
      {filteredMembers.length === 0 && !loading && <div className={S.empty}>
        {isEmpty(filters) ? 'No Appointments Scheduled For This Time Period' : emptyResultsMsg}
      </div>}
      {!loading && filteredMembers.map(m => <CalendarAssigneeRow
        key={`${m.assignee.kind}${m.assignee.id}`}
        member={m}
        onSelect={this.onSelect}
        dates={dates}
        expand={filteredMembers.length < 2}
        selectedDayStart={selectedDayStart}
        selectedDayEnd={selectedDayEnd}
        selectedAssignee={selectedAssignee}
        pendingAppointment={pendingAppointment}
        selectable={selectable}
        onReschedule={this.handleReschedule}
        role={role}
        isScheduleBoard={isScheduleBoard}
        applySmartScheduling={applySmartScheduling}
        onOpeningClick={this.onOpeningClick}
        relatedPhases={relatedPhases}
        setDelayedField={setDelayedField}
        isCcs={isCcs}
        phaseZip={phaseZip}
        phaseType={phaseType}
        phaseSource={phaseSource}
        phaseEstimatorId={phaseEstimatorId}
        phaseInstallerId={phaseInstallerId}
        manageVendorWorkrooms={manageVendorWorkrooms}
        employableType={employableType}
      />)}
    </div>);
  }
}

SchedulingCalendar.propTypes = {
  children: PropTypes.node,
  navigate: PropTypes.func.isRequired,
  className: PropTypes.string,
  dates: PropTypes.arrayOf(PropTypes.instanceOf(Moment)).isRequired,
  days: PropTypes.number.isRequired,
  selectable: PropTypes.bool,
  selectedDayStart: PropTypes.instanceOf(Moment),
  selectedDayEnd: PropTypes.instanceOf(Moment),
  selectedAssignee: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
  }),
  onSelect: PropTypes.func,
  pendingAppointment: AppointmentType,
  role: PropTypes.string,
  isScheduleBoard: PropTypes.bool,
  applySmartScheduling: PropTypes.bool,
  onOpeningClick: PropTypes.func,
  relatedPhases: PropTypes.array,
  schedule: ScheduleType.isRequired,
  setDelayedField: PropTypes.func,
  phaseZip: PropTypes.string,
  phaseType: PropTypes.string,
  phaseSource: PropTypes.string,
  phaseEstimatorId: PropTypes.number,
  phaseInstallerId: PropTypes.number,
  isCcs: PropTypes.bool,
  emptyResultsMsg: PropTypes.string,
  manageVendorWorkrooms: PropTypes.object,
  employableType: PropTypes.string,
  applySsFilters: PropTypes.bool,
  showMismatchedCrews: PropTypes.bool,
};

SchedulingCalendar.defaultProps = {
  filters: {},
  className: '',
  isScheduleBoard: false,
  relatedPhases: [],
};

export default connect(() => ({}), { navigate })(SchedulingCalendar);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/schedule/components/schedulingCalendar.jsx